import { Header } from "@/components/navigation/Header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, Download, FileText, Video, Presentation, BookOpen, Calendar, Eye } from "lucide-react";
import { useState } from "react";

const Collateral = () => {
  const [searchQuery, setSearchQuery] = useState("");

  const documents = [
    {
      title: "Intel Gaudi 3 AI Accelerator Architecture Guide",
      description: "Comprehensive technical overview of Gaudi 3 architecture and optimization strategies",
      type: "whitepaper",
      category: "Technical",
      size: "2.4 MB",
      pages: 28,
      date: "2024-01-15",
      downloads: "1.2K",
      featured: true
    },
    {
      title: "Dell XE7740 Server Deployment Best Practices",
      description: "Infrastructure deployment guide for AI inference workloads",
      type: "guide",
      category: "Infrastructure", 
      size: "1.8 MB",
      pages: 22,
      date: "2024-01-20",
      downloads: "856",
      featured: true
    },
    {
      title: "GenAI Inference Performance Benchmarks",
      description: "Performance comparison across different model sizes and hardware configurations",
      type: "report",
      category: "Performance",
      size: "3.1 MB", 
      pages: 45,
      date: "2024-02-01",
      downloads: "2.1K",
      featured: false
    },
    {
      title: "ROI Calculator for AI Infrastructure",
      description: "Financial analysis tool for calculating AI infrastructure return on investment",
      type: "tool",
      category: "Business",
      size: "0.8 MB",
      pages: 12,
      date: "2024-02-10",
      downloads: "654",
      featured: false
    },
    {
      title: "Container Optimization for Large Language Models",
      description: "Best practices for containerizing and optimizing LLM inference workloads",
      type: "guide",
      category: "Technical",
      size: "1.5 MB",
      pages: 18,
      date: "2024-02-15",
      downloads: "432",
      featured: false
    },
    {
      title: "AI Security and Compliance Framework",
      description: "Security considerations and compliance requirements for enterprise AI deployments",
      type: "whitepaper",
      category: "Security",
      size: "2.2 MB",
      pages: 35,
      date: "2024-02-20",
      downloads: "789",
      featured: false
    }
  ];

  const webinars = [
    {
      title: "Scaling AI Inference in the Enterprise",
      description: "Learn how to scale AI inference workloads with Intel Gaudi 3 and Dell infrastructure",
      duration: "45 mins",
      date: "2024-03-01",
      speakers: ["Dr. Sarah Chen", "Mike Rodriguez"],
      views: "3.2K",
      upcoming: false
    },
    {
      title: "Cost Optimization Strategies for GenAI",
      description: "Practical approaches to optimizing costs while maintaining performance",
      duration: "60 mins",
      date: "2024-03-15",
      speakers: ["Lisa Wang", "Tom Baker"],
      views: "1.8K", 
      upcoming: false
    },
    {
      title: "Future of AI Hardware: What's Next",
      description: "Upcoming innovations in AI hardware and their impact on inference performance",
      duration: "50 mins",
      date: "2024-04-01",
      speakers: ["Dr. Alex Kumar", "Jennifer Liu"],
      views: "0",
      upcoming: true
    }
  ];

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "whitepaper": return FileText;
      case "guide": return BookOpen;
      case "report": return Presentation;
      case "tool": return FileText;
      default: return FileText;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "whitepaper": return "bg-blue-500/20 text-blue-700 dark:text-blue-400";
      case "guide": return "bg-green-500/20 text-green-700 dark:text-green-400";
      case "report": return "bg-purple-500/20 text-purple-700 dark:text-purple-400";
      case "tool": return "bg-orange-500/20 text-orange-700 dark:text-orange-400";
      default: return "bg-muted text-muted-foreground";
    }
  };

  const filteredDocuments = documents.filter(doc =>
    doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredWebinars = webinars.filter(webinar =>
    webinar.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    webinar.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-8">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <h1 className="text-3xl font-bold mb-2">Resource Library</h1>
              <p className="text-muted-foreground text-lg">
                White papers, guides, case studies, and technical documentation
              </p>
            </div>

            <Tabs defaultValue="documents" className="space-y-6">
              <TabsList className="grid w-full grid-cols-3 lg:w-500">
                <TabsTrigger value="documents">Documents</TabsTrigger>
                <TabsTrigger value="webinars">Webinars</TabsTrigger>
                <TabsTrigger value="case-studies">Case Studies</TabsTrigger>
              </TabsList>

              <TabsContent value="documents" className="space-y-6">
                {/* Search */}
                <Card>
                  <CardContent className="pt-6">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search documents, topics, or categories..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Featured Documents */}
                {filteredDocuments.some(doc => doc.featured) && (
                  <div>
                    <h2 className="text-xl font-semibold mb-4">Featured Resources</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                      {filteredDocuments.filter(doc => doc.featured).map((doc) => {
                        const IconComponent = getTypeIcon(doc.type);
                        return (
                          <Card key={doc.title} className="hover:shadow-glow transition-smooth ring-2 ring-primary/20">
                            <CardHeader>
                              <div className="flex items-start space-x-3">
                                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                  <IconComponent className="h-5 w-5 text-primary" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <CardTitle className="text-base leading-tight">{doc.title}</CardTitle>
                                  <div className="flex items-center space-x-2 mt-2">
                                    <Badge className={getTypeColor(doc.type)}>
                                      {doc.type}
                                    </Badge>
                                    <Badge variant="outline">{doc.category}</Badge>
                                  </div>
                                </div>
                              </div>
                            </CardHeader>
                            <CardContent>
                              <CardDescription className="mb-4">
                                {doc.description}
                              </CardDescription>
                              
                              <div className="grid grid-cols-3 gap-2 mb-4 text-sm text-muted-foreground">
                                <div>{doc.pages} pages</div>
                                <div>{doc.size}</div>
                                <div>{doc.downloads} downloads</div>
                              </div>

                              <div className="flex items-center justify-between">
                                <span className="text-sm text-muted-foreground">
                                  {new Date(doc.date).toLocaleDateString()}
                                </span>
                                <Button size="sm" className="gap-2">
                                  <Download className="h-4 w-4" />
                                  Download
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* All Documents */}
                <div>
                  <h2 className="text-xl font-semibold mb-4">All Documents</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredDocuments.filter(doc => !doc.featured).map((doc) => {
                      const IconComponent = getTypeIcon(doc.type);
                      return (
                        <Card key={doc.title} className="hover:shadow-glow transition-smooth">
                          <CardHeader>
                            <div className="flex items-start space-x-3">
                              <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                                <IconComponent className="h-4 w-4 text-primary" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <CardTitle className="text-sm leading-tight">{doc.title}</CardTitle>
                                <div className="flex items-center space-x-1 mt-1">
                                  <Badge className={`text-xs ${getTypeColor(doc.type)}`}>
                                    {doc.type}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <CardDescription className="text-sm mb-3">
                              {doc.description}
                            </CardDescription>
                            
                            <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                              <span>{doc.pages}p • {doc.size}</span>
                              <span>{doc.downloads} downloads</span>
                            </div>

                            <div className="flex items-center justify-between">
                              <span className="text-xs text-muted-foreground">
                                {new Date(doc.date).toLocaleDateString()}
                              </span>
                              <Button size="sm" variant="outline" className="text-xs gap-1">
                                <Download className="h-3 w-3" />
                                Download
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="webinars" className="space-y-6">
                {/* Search */}
                <Card>
                  <CardContent className="pt-6">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search webinars..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Webinars */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {filteredWebinars.map((webinar) => (
                    <Card key={webinar.title} className="hover:shadow-glow transition-smooth">
                      <CardHeader>
                        <div className="flex items-start space-x-3">
                          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Video className="h-5 w-5 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <CardTitle className="text-base leading-tight">{webinar.title}</CardTitle>
                            <div className="flex items-center space-x-2 mt-2">
                              {webinar.upcoming ? (
                                <Badge className="bg-orange-500/20 text-orange-700 dark:text-orange-400">
                                  <Calendar className="h-3 w-3 mr-1" />
                                  Upcoming
                                </Badge>
                              ) : (
                                <Badge className="bg-green-500/20 text-green-700 dark:text-green-400">
                                  Available
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <CardDescription className="mb-4">
                          {webinar.description}
                        </CardDescription>
                        
                        <div className="space-y-2 mb-4 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Duration:</span>
                            <span className="font-medium">{webinar.duration}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Date:</span>
                            <span className="font-medium">
                              {new Date(webinar.date).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Speakers:</span>
                            <span className="font-medium text-right">
                              {webinar.speakers.join(", ")}
                            </span>
                          </div>
                          {!webinar.upcoming && (
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">Views:</span>
                              <span className="font-medium">{webinar.views}</span>
                            </div>
                          )}
                        </div>

                        <Button 
                          className="w-full gap-2" 
                          disabled={webinar.upcoming}
                        >
                          {webinar.upcoming ? (
                            <>
                              <Calendar className="h-4 w-4" />
                              Register
                            </>
                          ) : (
                            <>
                              <Eye className="h-4 w-4" />
                              Watch Now
                            </>
                          )}
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="case-studies" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Customer Case Studies</CardTitle>
                    <CardDescription>
                      Real-world implementations and success stories from our customers
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center py-12">
                      <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-medium mb-2">Case Studies Coming Soon</h3>
                      <p className="text-muted-foreground mb-6">
                        We're preparing detailed case studies showcasing successful GenAI deployments
                      </p>
                      <Button variant="outline" disabled>
                        View Case Studies
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Collateral;